import { useEffect } from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { useI18n } from '../context/useI18n';

export default function RegisterPlanPickerModal({
  open,
  plans = [],
  selectedPlan,
  priceForCurrency,
  title,
  subtitle = '',
  onSelect,
  onClose,
}) {
  const { t } = useI18n();
  const modalTitle = title || t('register.step3_title', 'Elige tu plan');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="form-modal-overlay register-plan-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="register-plan-modal-title"
      onClick={onClose}
    >
      <div
        className="form-modal register-plan-modal max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-header">
          <div className="min-w-0">
            <h4 id="register-plan-modal-title" className="font-semibold text-stone-100 truncate">
              {modalTitle}
            </h4>
            {subtitle ? (
              <p className="text-xs text-stone-400 mt-0.5 truncate">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="text-stone-400 hover:text-stone-100 shrink-0"
            onClick={onClose}
            aria-label={t('common.close', 'Cerrar')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="form-modal-content pt-0">
          {plans.length === 0 ? (
            <p className="text-sm register-option-muted py-4">
              {t('register.no_plans', 'No hay planes activos para este programa. Contacta a tu gimnasio o a soporte.')}
            </p>
          ) : (
            <div className="register-plan-modal-scroll notranslate" translate="no">
              {plans.map((plan) => {
                const active = selectedPlan?.nombre === plan.nombre;
                return (
                  <button
                    key={plan.nombre}
                    type="button"
                    className={`register-option w-full text-left p-4 rounded-xl border-2 ${
                      active ? 'register-option--active' : ''
                    }`}
                    onClick={() => {
                      onSelect?.(plan);
                      onClose?.();
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold register-option-title">{plan.display_nombre || plan.nombre}</p>
                        {plan.descripcion ? (
                          <p className="text-sm register-option-muted mt-1">{plan.descripcion}</p>
                        ) : null}
                        <p className="register-option-price text-sm mt-2">
                          {priceForCurrency(plan, 'COP')}
                          {Number(plan.precio_mensual_usd || 0) > 0
                            ? ` · ${priceForCurrency(plan, 'USD')}`
                            : ''}
                        </p>
                        {plan.is_couple ? (
                          <p className="text-xs text-amber-400 mt-1">
                            {t('register.couple_plan_badge', 'Plan de pareja (2 usuarios)')}
                          </p>
                        ) : null}
                      </div>
                      {active ? (
                        <span className="register-option-selected-badge shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
                          <CheckCircle2 className="w-4 h-4" aria-hidden />
                          Seleccionado
                        </span>
                      ) : (
                        <span className="register-option-unselected-hint shrink-0 text-xs register-option-muted">
                          Elegir
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="form-modal-footer">
          <button type="button" className="btn-secondary w-full" onClick={onClose}>
            {t('common.close', 'Cerrar')}
          </button>
        </div>
      </div>
    </div>
  );
}
